import { Fragment, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Label } from '@ttab/elephant-ui'
import { LoaderIcon } from '@ttab/elephant-ui/icons'
import { cn } from '@ttab/elephant-ui/utils'
import { NewsvalueMap } from '@/defaults'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import { groupByNewsvalue } from '../lib/groupByNewsvalue'

/**
 * One candidate row. A row already in the memo is dimmed rather than moved or
 * dropped: the column is the search result, and an item jumping out of it on
 * a click is what made the filters look like they were doing nothing.
 *
 * A row picked but still waiting on the batch bar's block choice is dimmed like
 * any other pick, but carries no background - only once it is actually in a
 * block does the row pick one up, so the editor can tell a pick that is locked
 * in from one that still needs a block chosen.
 *
 * That background is the same tinted one the tables use for a selected row,
 * not a grey: `accent` and `muted` are the same colour in both themes, so an
 * assigned row painted with `accent` was indistinguishable from any row the
 * pointer happened to be over.
 *
 * An assigned row hovers by thinning its own tint rather than by taking the
 * grey on top of it, the way the dialog's create button does with
 * `hover:bg-primary/90`: the alpha blends toward the page behind the row, so
 * the same class lightens the tint in light mode and darkens it in dark mode
 * without either theme needing its own colour.
 */
const CandidateRow = ({ item, isSelected, isAssigned, onToggle }: {
  item: MemoItem
  isSelected: boolean
  isAssigned: boolean
  onToggle: () => void
}): JSX.Element => (
  <li
    className={cn(
      'flex items-center gap-2 rounded px-1 cursor-pointer transition-colors',
      isAssigned
        ? 'bg-table-selected hover:bg-table-selected/80'
        : 'hover:bg-muted'
    )}
  >
    <Checkbox
      id={`memo-candidate-${item.id}`}
      checked={isSelected}
      onCheckedChange={onToggle}
    />

    <Label
      htmlFor={`memo-candidate-${item.id}`}
      className={cn(
        'grow min-w-0 font-normal leading-5 truncate',
        // The Label's own colour is already muted, so the dimming is an
        // opacity rather than another colour class that would do nothing.
        isSelected && 'opacity-60'
      )}
    >
      {item.headline}
      {!!item.sectionTitle && (
        <span className='text-muted-foreground'>{` – ${item.sectionTitle}`}</span>
      )}
    </Label>
  </li>
)

/**
 * The heading above a run of candidates sharing a newsvalue. The digit alone
 * is what the rest of the app shows a newsvalue as, next to the same signal
 * icon, so the word only goes to a screen reader - a bare "5" in the middle
 * of a list of headlines says nothing on its own.
 *
 * Stuck to the top of the scrolling list and given its own background so it
 * keeps reading as a heading rather than scrolling out of context - the list
 * beneath it is long enough that an editor scrolls past several of these.
 */
const NewsvalueHeading = ({ newsvalue }: {
  newsvalue: string
}): JSX.Element => {
  const { t } = useTranslation()
  const option = NewsvalueMap[newsvalue]
  const Icon = option?.icon

  return (
    <li className='sticky top-0 z-10 flex items-center gap-1 px-1 py-1 bg-muted text-xs font-medium text-muted-foreground'>
      {!!Icon && <Icon {...option.iconProps} size={14} />}
      <span className='sr-only'>{`${t('core:labels.newsvalue')} `}</span>
      {newsvalue}
    </li>
  )
}

/**
 * The spinner while the column is being fetched, in the box the rows it is
 * fetching will land in. Above the section it pushed every column down the page
 * on each date or filter change, which read as the dialog jumping rather than
 * as this list being busy.
 */
const LoadingRow = (): JSX.Element => {
  const { t } = useTranslation()

  return (
    <li className='flex items-center justify-center gap-2 p-2 text-sm text-muted-foreground'>
      <LoaderIcon size={16} strokeWidth={2} className='animate-spin' />
      {t('common:misc.loading')}
    </li>
  )
}

/** Everything the index returned for one column, under newsvalue headings. */
export const CandidateList = ({ items, isLoading, selectedIds, unassignedIds, onToggle }: {
  items: MemoItem[]
  /** Whether the fetch these rows come from is still out. */
  isLoading: boolean
  selectedIds: Set<string>
  /** Selected items still waiting on the batch bar's block choice. */
  unassignedIds: Set<string>
  onToggle: (item: MemoItem) => void
}): JSX.Element => {
  return (
    // A fixed height rather than a cap: a column that came back with three
    // items and one that came back with thirty are the same search result, and
    // a box that shrinks to what it found leaves the columns beside each other
    // at different heights - and moves the ones below it every time a filter
    // changes. The value is shared with the preview beside it, see MemoPreview.
    <ul className='flex flex-col gap-1 h-[30rem] overflow-y-auto outline-2 outline-gray-300 dark:outline-gray-700 rounded'>
      {isLoading && <LoadingRow />}

      {groupByNewsvalue(items).map((group) => (
        <Fragment key={group.newsvalue ?? 'no-newsvalue'}>
          {!!group.newsvalue && <NewsvalueHeading newsvalue={group.newsvalue} />}
          {group.items.map((item) => (
            <CandidateRow
              key={item.id}
              item={item}
              isSelected={selectedIds.has(item.id)}
              isAssigned={selectedIds.has(item.id) && !unassignedIds.has(item.id)}
              onToggle={() => onToggle(item)}
            />
          ))}
        </Fragment>
      ))}
    </ul>
  )
}
